import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cva } from "class-variance-authority";
import * as React from "react";
import { useState } from "react";

import type { DropDownMenuOption } from "./DropDownMenuOption";

import { cn } from "../../../lib/utils";

import { DROPDOWN_ANIMATIONS } from "../../../lib/styles";

const triggerVariants = cva(
	"inline-flex h-8 cursor-pointer items-center justify-center rounded-1 bg-transparent p-2 text-paragraph-3 disabled:cursor-not-allowed",
	{
		variants: {
			bordered: {
				true: "border border-neutral-3 enabled:hover:border-neutral-5 data-[state=open]:border-neutral-5",
				false: "border-none",
			},
		},
	}
);

const itemVariants = cva(
	"flex cursor-pointer items-center justify-between border-none bg-transparent p-2 text-paragraph-3 outline-hidden data-[highlighted]:bg-neutral-1",
	{
		variants: {
			active: {
				true: "bg-neutral-1",
			},
			disabled: {
				true: "cursor-not-allowed opacity-50",
			},
		},
	}
);

export type DropDownMenuProps = {
	/**
	 * options to display
	 */
	options: DropDownMenuOption[];
	/**
	 * active option key
	 */
	activeKey?: string;
	/**
	 * disabled menu
	 */
	disabled?: boolean;
	/**
	 * additional class wrapper
	 */
	className?: string;
	/**
	 * additional class wrapper for the menu
	 */
	menuClassName?: string;
	/**
	 * additional style wrapper
	 */
	style?: React.CSSProperties;
	/**
	 * onSelect Function
	 * @param _key
	 * @returns
	 */
	onSelect?: (_key: string) => void;

	/**
	 * close on select
	 * @default true
	 */
	closeOnSelect?: boolean;

	/**
	 * testid maps onto wrapper data-testid
	 */
	testid?: string;

	/**
	 * optional min width for the drop down menu options
	 */
	minWidth?: number | null;

	/**
	 * button Contents
	 */
	children?: React.ReactNode;

	/**
	 * placement of the dropdown menu
	 */
	placement?:
		| "bottom-end"
		| "bottom-start"
		| "top-end"
		| "top-start"
		| "right-start"
		| "right-end"
		| "left-start"
		| "left-end"
		| "bottom"
		| "top"
		| "right"
		| "left";

	/**
	 * if the dropdown menu button should have a border
	 */
	bordered?: boolean;
};

function parsePlacement(placement?: DropDownMenuProps["placement"]): {
	side?: "top" | "bottom" | "left" | "right";
	align?: "start" | "center" | "end";
} {
	if (!placement) return {};
	const parts = placement.split("-");
	const side = parts[0] as "top" | "bottom" | "left" | "right";
	const align = (parts[1] as "start" | "center" | "end") ?? "center";
	return { side, align };
}

export const DropDownMenu = ({
	options,
	className,
	menuClassName,
	style,
	activeKey,
	onSelect = () => {},
	disabled,
	testid,
	closeOnSelect = true,
	minWidth = null,
	children,
	placement,
	bordered = false,
}: DropDownMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const { side, align } = parsePlacement(placement);

	const handleOpenChange = (open: boolean) => {
		if (disabled) return;
		setIsOpen(open);
	};

	const handleItemSelect = (option: DropDownMenuOption) => (e: Event) => {
		if (option.disabled) return;
		if (!closeOnSelect) {
			e.preventDefault();
		}
		option.onClick?.();
		onSelect(option.key);
	};

	return (
		<div className={className} style={style} data-testid={testid}>
			<DropdownMenuPrimitive.Root open={isOpen} onOpenChange={handleOpenChange} modal={false}>
				<DropdownMenuPrimitive.Trigger asChild>
					<button
						type="button"
						data-testid="dropdown-button"
						disabled={disabled}
						className={cn(triggerVariants({ bordered }), className)}
					>
						{children}
					</button>
				</DropdownMenuPrimitive.Trigger>
				<DropdownMenuPrimitive.Portal>
					<DropdownMenuPrimitive.Content
						className={cn(
							"flex flex-col rounded-[4px] border border-solid border-neutral-3 bg-neutral-0",
							DROPDOWN_ANIMATIONS,
							"z-50",
							menuClassName
						)}
						side={side}
						align={align}
						sideOffset={8}
						style={{
							minWidth: minWidth ?? "var(--radix-dropdown-menu-trigger-width, 160px)",
						}}
						data-testid="floating-menu"
					>
						{options.map((option) => (
							<DropdownMenuPrimitive.Item
								key={option.key}
								className={cn(
									itemVariants({
										active: option.key === activeKey,
										disabled: option.disabled,
									})
								)}
								disabled={option.disabled}
								onSelect={handleItemSelect(option)}
								data-testid={`dropdown-option-${option.key}`}
							>
								{option.href ? (
									<a
										href={option.href}
										className="no-tooltip flex w-full items-center justify-between no-underline"
										data-testid={`dropdown-link-${option.key}`}
									>
										<p className="text-left text-paragraph-3 text-neutral-9">{option.label}</p>
										{option.right && (
											<div className="text-paragraph-3 text-neutral-9">{option.right}</div>
										)}
									</a>
								) : (
									<>
										<p className="text-left text-paragraph-3 text-neutral-9">{option.label}</p>
										{option.right && (
											<div className="text-paragraph-3 text-neutral-9">{option.right}</div>
										)}
									</>
								)}
							</DropdownMenuPrimitive.Item>
						))}
					</DropdownMenuPrimitive.Content>
				</DropdownMenuPrimitive.Portal>
			</DropdownMenuPrimitive.Root>
		</div>
	);
};
